// File: frontend/src/pages/Socrates/components/AIAgentsAdmin/AgentAccessControl/index.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faKey, faPlus, faSearch, 
  faEdit, faTrash, faUsers, faUser 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import SocratesCard from '../../common/SocratesCard';
import styles from './AgentAccessControl.module.css';

const AgentAccessControl = ({ agent }) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('permissions');

  // Mock permissions data
  const mockPermissions = [
    {
      id: 'perm_1',
      name: 'agent.read',
      description: 'Read agent configuration and status',
      granted: true,
      type: 'system'
    },
    {
      id: 'perm_2',
      name: 'agent.write',
      description: 'Modify agent configuration',
      granted: true,
      type: 'system'
    },
    {
      id: 'perm_3',
      name: 'artifacts.manage',
      description: 'Create, update, and delete artifacts',
      granted: false,
      type: 'feature'
    },
    {
      id: 'perm_4',
      name: 'variables.manage',
      description: 'Manage environment variables',
      granted: true,
      type: 'feature'
    }
  ];

  // Mock access control data
  const mockAccessControl = [
    {
      id: 'ac_1',
      type: 'user',
      name: 'John Doe',
      email: 'john.doe@company.com',
      role: 'admin',
      permissions: ['read', 'write', 'manage'],
      lastAccess: '2024-01-20T14:30:00Z'
    },
    {
      id: 'ac_2',
      type: 'user',
      name: 'Jane Smith',
      email: 'jane.smith@company.com',
      role: 'viewer',
      permissions: ['read'],
      lastAccess: '2024-01-19T10:15:00Z'
    },
    {
      id: 'ac_3',
      type: 'group',
      name: 'AI Team',
      members: 5,
      role: 'editor',
      permissions: ['read', 'write'],
      lastAccess: '2024-01-18T16:45:00Z'
    }
  ];

  const filteredPermissions = mockPermissions.filter(permission =>
    permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAccessControl = mockAccessControl.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const togglePermission = (permissionId) => {
    // Handle permission toggle
    console.log('Toggle permission:', permissionId);
  };

  const renderPermissions = () => (
    <div className={styles.permissionsList}>
      {filteredPermissions.map((permission) => (
        <div key={permission.id} className={styles.permissionItem}>
          <div className={styles.permissionInfo}>
            <div className={styles.permissionHeader}>
              <h4 className={styles.permissionName}>{permission.name}</h4>
              <div className={styles.permissionBadges}>
                <span className={`${styles.typeBadge} ${styles[permission.type]}`}>
                  {permission.type}
                </span>
              </div>
            </div>
            <p className={styles.permissionDescription}>
              {permission.description}
            </p>
          </div>
          <div className={styles.permissionToggle}>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={permission.granted}
                onChange={() => togglePermission(permission.id)}
              />
              <span className={styles.toggleSlider}></span>
            </label>
          </div>
        </div>
      ))}
    </div>
  );

  const renderAccessControl = () => (
    <div className={styles.accessList}>
      {filteredAccessControl.map((item) => (
        <div key={item.id} className={styles.accessItem}>
          <div className={styles.accessInfo}>
            <div className={styles.accessHeader}>
              <div className={styles.accessIcon}>
                <FontAwesomeIcon icon={item.type === 'user' ? faUser : faUsers} />
              </div>
              <div className={styles.accessDetails}>
                <h4 className={styles.accessName}>{item.name}</h4>
                {item.email && (
                  <p className={styles.accessEmail}>{item.email}</p>
                )}
                {item.members && (
                  <p className={styles.accessMembers}>
                    {item.members} {t('members')}
                  </p>
                )}
              </div>
              <div className={styles.accessBadges}>
                <span className={`${styles.roleBadge} ${styles[item.role]}`}>
                  {item.role}
                </span>
              </div>
            </div>
            <div className={styles.accessMeta}>
              <div className={styles.accessPermissions}>
                {item.permissions.map((perm, index) => (
                  <span key={index} className={styles.permissionTag}>
                    {perm}
                  </span>
                ))}
              </div>
              <span className={styles.lastAccess}>
                {t('last_access')}: {formatDate(item.lastAccess)}
              </span>
            </div>
          </div>
          <div className={styles.accessActions}>
            <button className={styles.actionButton} title={t('edit')}>
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button className={styles.actionButton} title={t('remove')}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.agentAccessControl}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>
            <FontAwesomeIcon icon={faKey} className={styles.titleIcon} />
            {t('access_control')}
          </h2>
          <p className={styles.subtitle}>
            {t('manage_access_for', { agentName: agent.display_name })}
          </p>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.createButton}>
            <FontAwesomeIcon icon={faPlus} />
            <span>{t('add_access')}</span>
          </button>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${activeTab === 'permissions' ? styles.active : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          {t('permissions')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'access' ? styles.active : ''}`}
          onClick={() => setActiveTab('access')}
        >
          {t('user_access')}
        </button>
      </div>

      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            placeholder={activeTab === 'permissions' ? t('search_permissions') : t('search_users')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <SocratesCard className={styles.accessCard}>
        <div className={styles.cardContent}>
          {activeTab === 'permissions' ? renderPermissions() : renderAccessControl()}
        </div>
      </SocratesCard>
    </div>
  );
};

export default AgentAccessControl;